/**
 * Tiny shared store between the Team breadcrumb bar (rendered by Header) and
 * the Team view. The bar owns the controls, the view owns the data; this
 * carries the filters, the Active / Archived / Trash list and one-shot
 * "export" / "refresh" requests between them without adding props to Header.
 */
import { useSyncExternalStore } from 'react';
import type { TeamLifecycle } from '../../types/Team';
import { DEFAULT_TEAM_FILTERS, type SideFilter, type TeamFilters } from '../../utils/teamView';

export type TeamTab = 'members' | 'logins';

export interface TeamUiState {
  filters: TeamFilters;
  lifecycle: TeamLifecycle;
  tab: TeamTab;
  exportNonce: number;
  refreshNonce: number;
}

const INITIAL: TeamUiState = { filters: DEFAULT_TEAM_FILTERS, lifecycle: 'active', tab: 'members', exportNonce: 0, refreshNonce: 0 };
let state: TeamUiState = INITIAL;
const listeners = new Set<() => void>();

function update(patch: Partial<TeamUiState>): void {
  state = { ...state, ...patch };
  listeners.forEach(l => l());
}

export const getTeamUi = (): TeamUiState => state;
export function subscribeTeamUi(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export const setTeamSide = (side: SideFilter): void => update({ filters: { ...state.filters, side } });
export const setTeamShowDeactivated = (showDeactivated: boolean): void => update({ filters: { ...state.filters, showDeactivated } });
export const setTeamLifecycle = (lifecycle: TeamLifecycle): void => update({ lifecycle });
export const setTeamTab = (tab: TeamTab): void => update({ tab });
export const requestTeamExport = (): void => update({ exportNonce: state.exportNonce + 1 });
export const requestTeamRefresh = (): void => update({ refreshNonce: state.refreshNonce + 1 });
export const resetTeamUi = (): void => update({ ...INITIAL, exportNonce: state.exportNonce, refreshNonce: state.refreshNonce });

export function useTeamUi(): TeamUiState {
  return useSyncExternalStore(subscribeTeamUi, getTeamUi, getTeamUi);
}
